// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Toast from './Toast';

/**
 * Toast Container Component
 * 
 * Manages multiple toast notifications with stacking
 * Use the exported showToast function to display toasts from anywhere
 */

let toastId = 0;
let addToastCallback = null;

// Global function to show toasts from anywhere in the app
export const showToast = (message, type = 'info', options = {}) => {
  if (addToastCallback) {
    addToastCallback(message, type, options);
  } else {
    console.warn('ToastContainer not mounted. Toast message:', message);
  }
};

function ToastContainer() {
  const [toasts, setToasts] = useState([]);

  const addToast = useCallback((message, type, options) => {
    const id = toastId++;
    const newToast = {
      id,
      message,
      type,
      duration: options.duration || 3000,
      icon: options.icon,
      position: options.position || 'top-right'
    };

    setToasts(prev => [...prev, newToast]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  // Register the addToast callback globally
  useEffect(() => {
    addToastCallback = addToast;
    return () => {
      addToastCallback = null;
    };
  }, [addToast]);

  // Group toasts by position
  const toastsByPosition = toasts.reduce((acc, toast) => {
    const pos = toast.position || 'top-right';
    if (!acc[pos]) acc[pos] = [];
    acc[pos].push(toast);
    return acc;
  }, {});

  return createPortal(
    <>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <div key={position} className={`toast-stack toast-stack-${position}`}>
          {positionToasts.map((toast, index) => (
            <Toast
              key={toast.id}
              message={toast.message}
              type={toast.type}
              duration={toast.duration}
              icon={toast.icon}
              position={position}
              onClose={() => removeToast(toast.id)}
            />
          ))}
        </div>
      ))}
    </>,
    document.body
  );
}

export default ToastContainer;
